import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Globe,
  Trophy,
  Award,
  Shield,
  Newspaper,
  BarChart3,
  Settings,
  Search,
  Bell,
  Menu,
  X,
  Plus,
  UserCheck,
  AlertTriangle,
  Flag
} from 'lucide-react';
import UserDropdown from '../common/UserDropdown';
import AdminPostCreator from './AdminPostCreator';
import api from '../../services/api';

interface AdminLayoutProps {
  children?: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPostCreator, setShowPostCreator] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notifCount, setNotifCount] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch admin notifications (pending coaches, reports, etc.)
  useEffect(() => {
    const fetchNotifCount = async () => {
      try {
        const [coachRes, reportRes] = await Promise.allSettled([
          api.get('/admin/users?status=pending&limit=1'),
          api.get('/reports?status=pending&limit=1')
        ]);
        let count = 0;
        if (coachRes.status === 'fulfilled' && coachRes.value.data?.data?.pagination?.total) {
          count += coachRes.value.data.data.pagination.total;
        }
        if (reportRes.status === 'fulfilled' && reportRes.value.data?.data?.pagination?.total) {
          count += reportRes.value.data.data.pagination.total;
        }
        setNotifCount(count);
      } catch {
        // silently fail
      }
    };
    fetchNotifCount();
    const interval = setInterval(fetchNotifCount, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch notification details when panel opens
  const loadNotifications = async () => {
    if (notifLoading) return;
    setNotifLoading(true);
    try {
      const items: any[] = [];
      const [coachRes, reportRes] = await Promise.allSettled([
        api.get('/admin/users?status=pending&limit=5'),
        api.get('/reports?status=pending&limit=5')
      ]);
      if (coachRes.status === 'fulfilled' && coachRes.value.data?.data?.users) {
        coachRes.value.data.data.users.forEach((u: any) => {
          items.push({
            id: u._id || u.id,
            type: 'coach_pending',
            title: `${u.firstName || u.name} wants to join as Coach`,
            time: u.createdAt,
            path: '/admin/users'
          });
        });
      }
      if (reportRes.status === 'fulfilled' && reportRes.value.data?.data?.reports) {
        reportRes.value.data.data.reports.forEach((r: any) => {
          items.push({
            id: r._id || r.id,
            type: 'report',
            title: `New report: ${r.reason || 'Content reported'}`,
            time: r.createdAt,
            path: '/admin/moderation'
          });
        });
      }
      setNotifications(items);
    } catch {
      setNotifications([]);
    } finally {
      setNotifLoading(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
    };
    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifications]);

  const toggleNotifications = () => {
    const next = !showNotifications;
    setShowNotifications(next);
    if (next) loadNotifications();
  };

  const getTimeAgo = (date: string) => {
    if (!date) return '';
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: Users, label: 'Users', path: '/admin/users' },
    { icon: Globe, label: 'Communities', path: '/admin/communities' },
    { icon: Trophy, label: 'Tournaments', path: '/admin/tournaments' },
    { icon: Award, label: 'Achievements', path: '/admin/achievements' },
    { icon: Shield, label: 'Moderation', path: '/admin/moderation' },
    { icon: Newspaper, label: 'My Posts', path: '/admin/posts' },
    { icon: BarChart3, label: 'Reports', path: '/admin/reports' },
    { icon: Settings, label: 'Settings', path: '/admin/settings' },
  ];

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    setSidebarOpen(false); // Close mobile sidebar after navigation
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      // Navigate to users page with search term
      navigate(`/admin/users?search=${encodeURIComponent(searchTerm.trim())}`);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-slate-800 transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:relative lg:flex lg:flex-col
      `}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-slate-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-semibold text-lg">Sportofolo Admin</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-gray-400 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav className="mt-6 px-3">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActivePath(item.path);

            return (
              <button
                key={item.path}
                onClick={() => handleMenuClick(item.path)}
                className={`
                  w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-left transition-colors duration-200
                  ${isActive
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-300 hover:bg-slate-700 hover:text-white'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main content */}
      <div className="flex-1">
        {/* Top header */}
        <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-500 hover:text-gray-700"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="text-2xl font-semibold text-gray-900">Admin Dashboard</h1>
            </div>

            <div className="flex items-center space-x-4">
              {/* Admin Post Button */}
              <button
                onClick={() => setShowPostCreator(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                title="Create Admin Post"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Post as Admin</span>
              </button>

              {/* Search bar */}
              <form onSubmit={handleSearch} className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search users, communities, events..."
                  className="pl-10 pr-4 py-2 w-80 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </form>

              {/* Notifications */}
              <div className="relative" ref={notifRef}>
                <button
                  onClick={toggleNotifications}
                  className="p-2 text-gray-500 hover:text-gray-700 relative"
                >
                  <Bell className="w-6 h-6" />
                  {notifCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {notifCount > 9 ? '9+' : notifCount}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 top-12 w-80 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900 text-sm">Notifications</h3>
                      {notifCount > 0 && (
                        <span className="text-xs text-gray-500">{notifCount} pending</span>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifLoading ? (
                        <div className="p-6 text-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="p-6 text-center text-gray-500 text-sm">
                          No pending notifications
                        </div>
                      ) : (
                        notifications.map((notif) => (
                          <button
                            key={notif.id}
                            onClick={() => {
                              setShowNotifications(false);
                              navigate(notif.path);
                            }}
                            className="w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-50 last:border-0"
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              notif.type === 'coach_pending' ? 'bg-blue-100 text-blue-600' : 'bg-red-100 text-red-600'
                            }`}>
                              {notif.type === 'coach_pending' ? (
                                <UserCheck className="w-4 h-4" />
                              ) : (
                                <Flag className="w-4 h-4" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-900 truncate">{notif.title}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{getTimeAgo(notif.time)}</p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                    {notifications.length > 0 && (
                      <div className="px-4 py-2 border-t border-gray-100">
                        <button
                          onClick={() => {
                            setShowNotifications(false);
                            navigate('/admin/moderation');
                          }}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          View all in Moderation →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* User Dropdown */}
              <UserDropdown />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6 bg-gray-50">
          {children || <Outlet />}
        </main>
      </div>

      {/* Admin Post Creator Modal */}
      <AdminPostCreator
        isOpen={showPostCreator}
        onClose={() => setShowPostCreator(false)}
        onPostCreated={() => {
          // Optionally refresh data or show success message
        }}
      />
    </div>
  );
};

export default AdminLayout;